import { BlockBlobClient } from "@azure/storage-blob";
import { randomId } from "@mui/x-data-grid-generator";
import getAxiosFactory from "./customAxios";
import config from "../config";

const containerName = `uploads`;

export const ATTACHMENT_KIND_MEETING_MINUTES = "meeting-minutes";
export type AttachmentKind = typeof ATTACHMENT_KIND_MEETING_MINUTES;

export interface Attachment {
  id: string;
  url: string;
  name: string;
  displayName: string;
  creatorEmail?: string;
  sessionId?: string;
  taskId?: string;
  external?: boolean;
  kind?: AttachmentKind;
}

const getBlobClient = (
  blobName: string,
  token: string,
  storageAccountName: string,
) => {
  const blobUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobName}?${token}`;
  console.info(
    `[attachmentService] Creating BlockBlobClient with URL: ${blobUrl.substring(0, 150)}...`,
  );
  return new BlockBlobClient(blobUrl);
};

const getContainerSASToken = async (): Promise<{
  token: string;
  storageAccountName: string;
}> => {
  const factory = await getAxiosFactory(
    config.api.baseUrl,
    "/blobs/container",
  )();
  const response = await factory.request({
    method: "POST",
  });
  return {
    token: response.data.token,
    storageAccountName: response.data.storageAccountName,
  };
};

const getBlobSASToken = async (
  meetingId: string | undefined,
  fileName: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
): Promise<{ token: string; storageAccountName: string }> => {
  let url;
  const encodedFileName = encodeURIComponent(fileName);
  if (sessionId && taskId) {
    url = `/blobs/blob/session/${sessionId}/task/${taskId}/${encodedFileName}`;
  } else if (sessionId) {
    url = `/blobs/blob/session/${sessionId}/${encodedFileName}`;
  } else if (meetingId && itemId) {
    url = `/blobs/blob/${meetingId}/${itemId}/${encodedFileName}`;
  } else if (meetingId) {
    url = `/blobs/blob/${meetingId}/${encodedFileName}`;
  } else if (itemId) {
    url = `/blobs/blob/item/${itemId}/${encodedFileName}`;
  } else {
    throw new Error(
      "Either meetingId, itemId, or sessionId must be provided to get blob SAS token",
    );
  }
  const factory = await getAxiosFactory(config.api.baseUrl, url)();
  const response = await factory.request({
    method: "POST",
  });
  return {
    token: response.data.token,
    storageAccountName: response.data.storageAccountName,
  };
};

export const createBlobInContainer = async (
  prefix: string,
  file: File,
  meetingId?: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
): Promise<Attachment> => {
  try {
    const blobName = prefix + file.name;
    const { token: blobToken, storageAccountName } = await getBlobSASToken(
      meetingId,
      file.name,
      itemId,
      sessionId,
      taskId,
    );
    const blobClient = getBlobClient(blobName, blobToken, storageAccountName);
    // set mimetype as determined from browser with file upload control
    const options = { blobHTTPHeaders: { blobContentType: file.type } };
    await blobClient.uploadData(file, options);
    return {
      id: randomId(),
      url: `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobName}`,
      name: blobName,
      displayName: file.name,
    };
  } catch (error) {
    console.error(`Error creating blob "${prefix + file.name}":`, error);
    throw error;
  }
};

export const deleteBlobFromContainer = async (
  meetingId: string | undefined,
  fullBlobName: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
): Promise<void> => {
  try {
    const fileName = fullBlobName.substring(fullBlobName.lastIndexOf("/") + 1);
    const { token: blobToken, storageAccountName } = await getBlobSASToken(
      meetingId,
      fileName,
      itemId,
      sessionId,
      taskId,
    );
    const blobClient = getBlobClient(
      fullBlobName,
      blobToken,
      storageAccountName,
    );
    await blobClient.delete();
  } catch (error) {
    console.error(`Error deleting blob "${fullBlobName}":`, error);
  }
};

export const decorateBlobs = async (
  attachments: Attachment[],
): Promise<Attachment[]> => {
  const { token: containerToken } = await getContainerSASToken();
  return attachments.map((a) => {
    const safeDisplayName = a.displayName || a.name || a.url;
    const external = a.external === true;
    if (external) {
      return { ...a, displayName: safeDisplayName, external };
    }
    return {
      ...a,
      displayName: safeDisplayName,
      external,
      url: a.url + `?${containerToken}`,
    };
  });
};

const getAttachmentApiUrl = (
  meetingId?: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
  attachmentId?: string,
): string => {
  let url: string;

  if (sessionId && taskId) {
    url = `/attachments/session/${sessionId}/task/${taskId}`;
  } else if (sessionId) {
    url = `/attachments/session/${sessionId}`;
  } else if (meetingId && itemId) {
    url = `/attachments/${meetingId}/item/${itemId}`;
  } else if (meetingId) {
    // The backend uses the same route for both meeting-level and item-level attachments.
    // Passing "undefined" targets the meeting itself (itemId does not exist on the record).
    url = `/attachments/${meetingId}/item/undefined`;
  } else if (itemId) {
    url = `/attachments/item/${itemId}`;
  } else {
    throw new Error(
      "meetingId, itemId, or sessionId is required to work with attachments",
    );
  }

  return attachmentId ? `${url}/${attachmentId}` : url;
};

export const getAttachments = async (
  meetingId?: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
): Promise<Attachment[]> => {
  const url = getAttachmentApiUrl(meetingId, itemId, sessionId, taskId);
  const factory = await getAxiosFactory(config.api.baseUrl, url)();
  const response = await factory.request({
    method: "GET",
  });
  const attachments = Array.isArray(response.data) ? response.data : [];
  return decorateBlobs(attachments);
};

export const createAttachment = async (
  attachment: Attachment,
  meetingId?: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
): Promise<Attachment> => {
  const url = getAttachmentApiUrl(meetingId, itemId, sessionId, taskId);
  const factory = await getAxiosFactory(config.api.baseUrl, url)();
  const response = await factory.request({
    method: "POST",
    data: attachment,
  });

  return response.data;
};

export const deleteAttachment = async (
  attachment: Attachment,
  meetingId?: string,
  itemId?: string,
  sessionId?: string,
  taskId?: string,
): Promise<void> => {
  const url = getAttachmentApiUrl(
    meetingId,
    itemId,
    sessionId,
    taskId,
    attachment.id,
  );
  const factory = await getAxiosFactory(config.api.baseUrl, url)();
  await factory.request({
    method: "DELETE",
  });
};

/**
 * Uploads a generated meeting-minutes PDF to Azure Blob Storage under
 * `meetings/{meetingId}/minutes-{timestamp}.pdf` and upserts the
 * corresponding AttachmentModel row (kind = "meeting-minutes").
 *
 * Returns the persisted attachment with a SAS-decorated URL ready for download.
 */
export const uploadMeetingMinutes = async (
  meetingId: string,
  pdfBlob: Blob,
  displayName: string,
): Promise<Attachment> => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const blobFileName = `minutes-${timestamp}.pdf`;
  const file = new File([pdfBlob], blobFileName, { type: "application/pdf" });

  // Blob path must match the one the SAS token is signed for: meetings/{meetingId}/{fileName}
  const blob = await createBlobInContainer(
    `meetings/${meetingId}/`,
    file,
    meetingId,
  );

  const persisted = await createAttachment(
    {
      ...blob,
      displayName,
      kind: ATTACHMENT_KIND_MEETING_MINUTES,
    },
    meetingId,
  );

  const [decorated] = await decorateBlobs([persisted]);
  return decorated;
};

/**
 * Returns the meeting-minutes attachment for a meeting (with SAS-decorated URL),
 * or null if none has been generated yet.
 */
export const getMeetingMinutes = async (
  meetingId: string,
): Promise<Attachment | null> => {
  const factory = await getAxiosFactory(
    config.api.baseUrl,
    `/attachments/${meetingId}/minutes`,
  )();
  try {
    const response = await factory.request({ method: "GET" });
    if (!response.data) {
      return null;
    }
    const [decorated] = await decorateBlobs([response.data]);
    return decorated;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404) {
      return null;
    }
    throw err;
  }
};
